/** Locales, their Intl tags and the detection order (03 §14.3, 06 §14.2). */

/**
 * Every locale the product is planned for (README). The `Locale` type, the D1 `user.locale` column and the Intl
 * tables keep the full set, so the additional-languages release only extends `LOCALES` (ADR 0033).
 */
export const ALL_LOCALES = ['en', 'de', 'hu', 'cs', 'sk', 'sl'] as const;

/** A planned locale. */
export type Locale = (typeof ALL_LOCALES)[number];

/** The shipped locales: `en` (the master) and `de` (ADR 0033). The switcher and `languages.xml` list these. */
export const LOCALES: readonly Locale[] = Object.freeze(['en', 'de'] as const);

/** The Intl locale per locale (06 §14.2); `strings/languages.xml` must agree (checked by `i18n:check`). */
export const INTL_LOCALES: Readonly<Record<Locale, string>> = Object.freeze({
  en: 'en-GB',
  de: 'de-AT',
  hu: 'hu-HU',
  cs: 'cs-CZ',
  sk: 'sk-SK',
  sl: 'sl-SI',
});

/** Each locale's own name, as the switcher lists it (03 §14.3). */
export const NATIVE_NAMES: Readonly<Record<Locale, string>> = Object.freeze({
  en: 'English',
  de: 'Deutsch',
  hu: 'Magyar',
  cs: 'Čeština',
  sk: 'Slovenčina',
  sl: 'Slovenščina',
});

/** True if `x` is a shipped locale code (one of `LOCALES`). */
export function isLocale(x: unknown): x is Locale {
  return typeof x === 'string' && (LOCALES as readonly string[]).includes(x);
}

/** The inputs of locale detection, all optional. */
export interface LocaleHints {
  /** `user.locale`, when signed in (P3). */
  readonly user?: string | null | undefined;
  /** The `locale` cookie set by the switcher. */
  readonly cookie?: string | null | undefined;
  /** `navigator.languages`. */
  readonly languages?: readonly string[] | undefined;
}

/** The best match by primary subtag (`de-AT` → `de`), or null. */
function matchLanguage(tags: readonly string[]): Locale | null {
  for (const tag of tags) {
    const primary = tag.split(/[-_]/)[0]?.toLowerCase();
    if (isLocale(primary)) {
      return primary;
    }
  }
  return null;
}

/** Detection order (03 §14.3): `user.locale` → the `locale` cookie → `navigator.languages` → `en`. */
export function pickLocale(hints: LocaleHints): Locale {
  if (isLocale(hints.user)) {
    return hints.user;
  }
  if (isLocale(hints.cookie)) {
    return hints.cookie;
  }
  return matchLanguage(hints.languages ?? []) ?? 'en';
}
